// opervex-frontend/src/pages/Accounting/Vendors.js

import React, { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { FaPencilAlt, FaPlus, FaSave, FaSearch } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import { useParams } from "react-router-dom";
import useOpervex from "../../hooks/useOpervex";
import { SearchHelpModal } from "../../utils/SearchHelp";
import ConfirmService from "../../components/ConfirmationModal/ConfirmService";
import { useAuth } from "../../contexts/AuthContext";
import useCommandActions, { DEFAULT_ACTIONS } from "../../hooks/useCommandActions";
import MaskedFormControl from "react-bootstrap-maskedinput";

const rolePageRequire = "VENDORS";

const emptyData = () => ({
  id: "",
  name: "",
  type: "",
  document: "",
  email: "",
  phone: "",
  disabled: false,
});

const Vendors = () => {
  const { loadingGlobal } = useAuth();
  const { actions, commandAction } = useCommandActions(rolePageRequire);
  const [data, setData] = useState(emptyData());
  const { id } = useParams();
  const api = useOpervex();
  const [disabled, setDisabled] = useState(true);

  const load = async (recordId) => {
    setDisabled(true);

    if (!recordId) return;

    const result = await api.Accounting.Vendors.findId(recordId);

    if (result?.errors) return await ConfirmService.show("Aviso", result.errors[0]?.message || "Fornecedor não encontrado", true);

    if (result?.id) commandAction({ ...DEFAULT_ACTIONS, create: true, change: true });
    else commandAction({ ...DEFAULT_ACTIONS, create: true });

    setData(result);
  };

  useEffect(() => {
    if (id) load(id);
    else commandAction({ ...DEFAULT_ACTIONS, create: true });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadingGlobal) {
    return <div>Carregando...</div>;
  }

  const change = (event) => {
    const { name, value, type, checked } = event.target;
    setData((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    const result = data.id ? await api.Accounting.Vendors.update(data.id, data) : await api.Accounting.Vendors.create(data);
    if (result?.errors) return ConfirmService.show("Aviso", result.errors, true);

    await load(data.id || result.id);

    await ConfirmService.show("Aviso", "Fornecedor salvo com sucesso!", true);
  };

  const search = async () => {
    const item = await SearchHelpModal({
      urlData: "/Accounting/Vendors/SearchHelp",
      schema: await api.Accounting.Vendors.schema(),
    });
    if (item?.id) load(item.id);
  };

  const handleCreate = async () => {
    setData(emptyData());
    setDisabled(false);

    commandAction({ ...DEFAULT_ACTIONS, save: true });
  };

  const handleChange = async () => {
    setDisabled(false);
    commandAction({ ...DEFAULT_ACTIONS, save: true });
  };

  const handleCancel = async () => {
    if (data.id === "") {
      setData(emptyData());
      setDisabled(true);
      commandAction({ ...DEFAULT_ACTIONS, create: true });
    } else {
      load(data.id);
    }
  };

  const documentConfig = {
    label: data.type === "F" ? "CPF" : data.type === "J" ? "CNPJ" : "CPF/CNPJ",

    mask: data.type === "F" ? "111.111.111-11" : "11.111.111/1111-11",
  };

  return (
    <Container className="mt-2">
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">Fornecedores</h5>
            </Col>
            <Col className="text-end">
              <Button variant="success" size="sm" className="me-2" onClick={handleCreate} hidden={!actions.create}>
                <FaPlus /> Novo
              </Button>
              <Button variant="primary" size="sm" className="me-2" onClick={handleChange} hidden={!actions.change}>
                <FaPencilAlt /> Alterar
              </Button>
              <Button variant="danger" size="sm" className="me-2" onClick={handleCancel} hidden={!actions.save}>
                <MdOutlineCancel /> Cancelar
              </Button>
              <Button variant="primary" size="sm" className="me-2" onClick={handleSave} hidden={!actions.save}>
                <FaSave /> Salvar
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row className="g-2 mb-2">
            <Col md={2}>
              <Form.Label>Código</Form.Label>
              <InputGroup>
                <Form.Control size="sm" value={data.id} disabled />
                <InputGroup.Text role="button" onClick={search}>
                  <FaSearch />
                </InputGroup.Text>
              </InputGroup>
            </Col>
            <Col md={6}>
              <Form.Label>Nome / razão social</Form.Label>
              <Form.Control size="sm" name="name" value={data.name} onChange={change} disabled={disabled} />
            </Col>
            <Col md={2} className="d-flex align-items-end pb-1">
              <Form.Check label="Inativo" name="disabled" checked={data.disabled} onChange={change} disabled={disabled} />
            </Col>
          </Row>
          <Row className="g-2 mb-2">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Tipo</Form.Label>
                <Form.Select size="sm" value={data.type} onChange={change} name="type" disabled={disabled}>
                  <option value="">Selecione...</option>
                  <option key="F" value="F">
                    Física
                  </option>
                  <option key="J" value="J">
                    Jurídica
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Label>{documentConfig.label}</Form.Label>
              <MaskedFormControl
                size="sm"
                mask={documentConfig.mask}
                name="document"
                value={data.document}
                onChange={change}
                disabled={disabled}
              />
            </Col>
            <Col md={2}>
              <Form.Label>Telefone</Form.Label>
              <MaskedFormControl
                type="text"
                size="sm"
                mask="(11) 11111-1111"
                name="phone"
                value={data.phone}
                onChange={change}
                disabled={disabled}
              />
            </Col>
            <Col md={5}>
              <Form.Label>E-mail</Form.Label>
              <Form.Control size="sm" type="email" name="email" value={data.email} onChange={change} disabled={disabled} />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Vendors;
